import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Slider, Typography, FormControlLabel, Checkbox, Radio } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import classnames from 'classnames';
import { t, defineMessage } from '@lingui/macro';
import { i18n } from '@lingui/core';
import styles from '../FilterSidebar.module.css';
import { StandardRadioGroup } from '../StandardRadioGroup';
import * as T from '../../../constants/types';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import { FACT_REBALANCE_COLORS_LEGEND, FACT_REBALANCE_SIZE_LEGEND } from '../../../constants/legends';

const DAY_MODE_OPTIONS = [
    { value: 'day',    label: defineMessage({ message: 'Сутки' }) },
    { value: 'period', label: defineMessage({ message: 'Временной интервал' }) },
];
const LAYER_ID = 'Фактический ребаланс';
// Конфиг ролей: ключ → поля rebalance_from_X / rebalance_to_X в тайлах
const ROLE_CONFIG = [
    { key: 'sc',     label: 'Service Car' },
    { key: 'ranger', label: 'Ranger' },
    { key: 'other',  label: 'Other' },
];
/**
 * Строит MapBox-фильтр, объединяющий period_index, выбранные роли и минимальное число перевозок.
 * Показывает точку, если period совпадает И хотя бы одна из выбранных ролей
 * имеет rebalance_from_X > 0 или rebalance_to_X > 0,
 * И суммарное число перевозок (rebalance_from + rebalance_to) >= minCount.
 * Экспортируется для использования в Map.jsx (перезагрузка тайлов).
 */
export const buildRebalanceFilter = (period, roles, minCount = 0, dayMode = 'period') => {
    const activeRoles = ROLE_CONFIG.filter(r => roles[r.key]);
    // Если все роли сняты — скрываем все точки
    if (activeRoles.length === 0) {
        return [ 'boolean', false ]; // никогда не истинно
    }

    const totalExpr = [
        '+',
        [ 'to-number', [ 'get', 'rebalance_from' ], 0 ],
        [ 'to-number', [ 'get', 'rebalance_to' ],   0 ],
    ];
    const minCountCondition = minCount > 0
        ? [ [ '>=', totalExpr, minCount ] ]
        : [];

    // period_index фильтруется на сервере (query_param 'period'), клиентский фильтр не нужен.
    // Оставляем только фильтрацию по ролям и minCount.

    // Если все роли выбраны — фильтруем только по minCount
    if (activeRoles.length === ROLE_CONFIG.length) {
        if (minCountCondition.length === 0) return [ 'all' ]; // показываем всё
        return minCountCondition.length === 1 ? minCountCondition[0] : [ 'all', ...minCountCondition ];
    }
    // Иначе: (role1_from>0 OR role1_to>0 OR ...) AND total >= minCount
    const roleConditions = activeRoles.flatMap(r => [
        [ '>', [ 'to-number', [ 'get', `rebalance_from_${r.key}` ], 0 ], 0 ],
        [ '>', [ 'to-number', [ 'get', `rebalance_to_${r.key}` ],   0 ], 0 ],
    ]);
    return [
        'all',
        [ 'any', ...roleConditions ],
        ...minCountCondition,
    ];
};
const FactRebalanceFilterComponent = ({
    selectedRebalancePeriod,
    selectedRebalanceRoles,
    setSelectedRebalanceRoles,
    selectedRebalanceMinCount,
    setSelectedRebalanceMinCount,
    selectedRebalanceDayMode,
    setSelectedRebalanceDayMode,
}) => {
    const [ isRolesExpanded, setIsRolesExpanded ] = useState(false);
    const [ isMinCountExpanded, setIsMinCountExpanded ] = useState(false);
    // Обновляем фильтр MapBox при смене периода, ролей, минимального числа перевозок или режима суток
    React.useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        const mapInstance = window.__map;
        if (!mapInstance) return;
        try {
            const layer = mapInstance.getLayer(LAYER_ID);
            if (!layer) return;
            mapInstance.setFilter(LAYER_ID, buildRebalanceFilter(selectedRebalancePeriod, selectedRebalanceRoles, selectedRebalanceMinCount, selectedRebalanceDayMode));
        } catch (err) {
            // игнорируем — слой временно отсутствует (идёт перезагрузка тайлов)
        }
    }, [ selectedRebalancePeriod, selectedRebalanceRoles, selectedRebalanceMinCount, selectedRebalanceDayMode ]);

    return (
        <div>
            {/* Дата и период суток выбираются на таймлайне внизу карты (FactRebalanceTimeline):
                в режиме «Сутки» — курсор-дата + степпер года, в режиме «Период» — курсор-период
                суток + степпер даты. */}
            {/* Режим агрегации: Сутки / Период — переключает режим таймлайна */}
            <div
                className={styles.legHead}
                style={{ marginTop: 10 }}
            >{t`Временной диапазон`}:</div>
            <StandardRadioGroup
                value={selectedRebalanceDayMode}
                onChange={e => setSelectedRebalanceDayMode(e.target.value)}
            >
                {DAY_MODE_OPTIONS.map(({ value, label }) => (
                    <FormControlLabel
                        key={value}
                        className={styles.isochroneFilterLabel}
                        control={
                            <Radio
                                checked={selectedRebalanceDayMode === value}
                                onChange={e => setSelectedRebalanceDayMode(e.target.value)}
                                value={value}
                                className={classnames(styles.isochroneFilterInput, {
                                    [styles.checkedIsochroneFilterInput]: selectedRebalanceDayMode === value,
                                })}
                                size="small"
                            />
                        }
                        label={i18n._(label)}
                    />
                ))}
            </StandardRadioGroup>
            {/* Чекбоксы ролей — раскрывающийся блок */}
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                <div
                    className={classnames(styles.legHead, styles.expandableLegHead, {
                        [styles.expandableLegHeadOpen]: isRolesExpanded,
                    })}
                    onClick={() => setIsRolesExpanded(!isRolesExpanded)}
                    style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', padding: '6px 0' }}
                >
                    {t`Группы ролей`}
                    <Icon style={{ marginLeft: 6 }}>expand_more</Icon>
                </div>
                {isRolesExpanded && (
                    <div>
                        {ROLE_CONFIG.map(({ key, label }) => (
                            <FormControlLabel
                                key={key}
                                style={{ display: 'flex', alignItems: 'center', marginBottom: 2, marginLeft: 0 }}
                                control={
                                    <Checkbox
                                        checked={selectedRebalanceRoles[key]}
                                        onChange={e => setSelectedRebalanceRoles({
                                            ...selectedRebalanceRoles,
                                            [key]: e.target.checked,
                                        })}
                                        size="small"
                                        style={{ color: 'white', padding: '2px 6px 2px 0' }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="body2"
                                        style={{ fontSize: '12px' }}
                                    >{label}</Typography>
                                }
                            />
                        ))}
                    </div>
                )}
                <div style={{ marginBottom: 4 }} />
            </div>
            {/* Минимальное число перевозок — раскрывающийся блок */}
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                <div
                    className={classnames(styles.legHead, styles.expandableLegHead, {
                        [styles.expandableLegHeadOpen]: isMinCountExpanded,
                    })}
                    onClick={() => setIsMinCountExpanded(!isMinCountExpanded)}
                    style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', padding: '6px 0' }}
                >
                    {t`Число перевозок`}
                    <Icon style={{ marginLeft: 6 }}>expand_more</Icon>
                </div>
                {isMinCountExpanded && (
                    <div>
                        <Typography
                            gutterBottom
                            style={{ marginTop: 4 }}
                        >
                            {t`не менее`}: <strong>{selectedRebalanceMinCount}</strong>
                        </Typography>
                        <Slider
                            value={selectedRebalanceMinCount}
                            min={0}
                            max={50}
                            step={1}
                            marks={[ 0, 10, 20, 30, 40, 50 ].map(v => ({ value: v }))}
                            valueLabelDisplay="off"
                            onChange={(_, v) => setSelectedRebalanceMinCount(v)}
                        />
                    </div>
                )}
                <div style={{ marginBottom: 4 }} />
            </div>
        </div>
    );
};
const mapStateToProps = ({ map: { selectedRebalancePeriod, selectedRebalanceRoles, selectedRebalanceMinCount, selectedRebalanceDayMode } }) => ({
    selectedRebalancePeriod,
    selectedRebalanceRoles,
    selectedRebalanceMinCount,
    selectedRebalanceDayMode,
});
const mapDispatchToProps = dispatch => ({
    setSelectedRebalanceRoles: roles => dispatch({
        type: T.SET_SELECTED_REBALANCE_ROLES,
        payload: { selectedRebalanceRoles: roles },
    }),
    setSelectedRebalanceMinCount: count => dispatch({
        type: T.SET_SELECTED_REBALANCE_MIN_COUNT,
        payload: { selectedRebalanceMinCount: count },
    }),
    setSelectedRebalanceDayMode: mode => dispatch({
        type: T.SET_SELECTED_REBALANCE_DAY_MODE,
        payload: { selectedRebalanceDayMode: mode },
    }),
});
const ConnectedFilter = connect(mapStateToProps, mapDispatchToProps)(FactRebalanceFilterComponent);

export const FactRebalanceFilter = withDisplayedLayersCheck(
    ConnectedFilter,
    t`Фактический ребаланс`
);

// ─── Легенда ────────────────────────────────────────────────
const FactRebalanceLegendComponent = () => (
    <div style={{ padding: '0 10px' }}>
        <LegendRenderer config={FACT_REBALANCE_SIZE_LEGEND} />
        <div style={{ marginBottom: 12 }} />
        <LegendRenderer config={FACT_REBALANCE_COLORS_LEGEND} />
        <div style={{ marginBottom: 32 }} />
    </div>
);

export const FactRebalanceLegend = withDisplayedLayersCheck(
    FactRebalanceLegendComponent,
    t`Фактический ребаланс`
);
